import { learnerService } from "@/api/learnerService";
import { learnerTicketService } from "@/api/learnerTicketService";
import type { LearnerResponse } from "@/resources/learner";
import type { BaseTicketWithId } from "@/resources/ticket";

/**
 * Fetch learners and keep the data in memory to avoid fetching the same learner multiple times.
 */

const loadedLearners: { [key: string]: LearnerResponse | null } = {};
const loadedLearnerTickets: { [key: string]: BaseTicketWithId } = {};

export async function getLearnersByIds(
  ids: string[],
): Promise<LearnerResponse[]> {
  try {
    const cachedLearners: LearnerResponse[] = [];
    const missingIds: string[] = [];

    ids.forEach((id) => {
      if (loadedLearners[id]) {
        cachedLearners.push(loadedLearners[id] as LearnerResponse);
      } else {
        missingIds.push(id);
      }
    });

    if (missingIds.length === 0) {
      return cachedLearners;
    }

    const batchLearners = await learnerService.getByIds(missingIds);

    batchLearners.forEach((learner) => {
      loadedLearners[learner.id] = learner;
    });

    const result = [...cachedLearners, ...batchLearners];
    return result;
  } catch (error) {
    console.error("Failed to fetch learners batch:", error);
    return [];
  }
}

export function invalidateLearnerTicketCache(
  learnerId: string,
  couponCode: string,
  campaignId: string,
): void {
  const key = `${learnerId}-${couponCode}-${campaignId}`;
  delete loadedLearnerTickets[key];
}

export async function getLearnerTicketFromCouponCode(
  learnerId: string,
  couponCode: string,
  campaignId: string,
): Promise<BaseTicketWithId | undefined> {
  const key = `${learnerId}-${couponCode}-${campaignId}`;
  if (loadedLearnerTickets[key]) {
    return loadedLearnerTickets[key];
  }

  const learnerTickets = await learnerTicketService.list(learnerId);

  const matchingTicket = learnerTickets.find(
    (ticket) =>
      ticket.customerOptions.couponCode === couponCode &&
      ticket.campaignId === campaignId,
  );

  if (matchingTicket) {
    loadedLearnerTickets[key] = matchingTicket;
    return matchingTicket;
  }

  // Explicitly return undefined when no ticket is found
  return undefined;
}

const SYNTHETIC_EMAIL_SUFFIX = "@learners.equ.ai";
const SYNTHETIC_EMAIL_PREFIX = "learner_";

export function extractUsernameFromSyntheticEmail(
  email: string,
): string | null {
  if (
    !email.endsWith(SYNTHETIC_EMAIL_SUFFIX) ||
    !email.startsWith(SYNTHETIC_EMAIL_PREFIX)
  ) {
    return null;
  }
  return email.slice(
    SYNTHETIC_EMAIL_PREFIX.length,
    email.length - SYNTHETIC_EMAIL_SUFFIX.length,
  );
}
